import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../assets/ThemeContext';
import { useAuth } from '../../assets/AuthContext';

const PAGE_TITLES = {
  '/':        { title: 'Operational Overview', sub: 'Active assessments & project status' },
  '/projects':{ title: 'Projects',             sub: 'Manage obstacle safeguarding assessments' },
  '/data':    { title: 'Data Manager',         sub: 'ARP · CNS · THR reference tables' },
  '/settings':{ title: 'Settings',             sub: 'Preferences & interface options' },
};

function initials(user) {
  if (!user) return '?';
  if (user.full_name) {
    const parts = user.full_name.trim().split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
  }
  return user.username.slice(0, 2).toUpperCase();
}

export default function Topbar({ sidebarWidth }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  let meta = PAGE_TITLES[loc.pathname];
  if (!meta) {
    if (loc.pathname.endsWith('/assess'))           meta = { title: 'Assessment', sub: 'Map view · ARP & CNS proximity analysis' };
    else if (loc.pathname.startsWith('/projects/')) meta = { title: 'Project',    sub: 'Obstacle loading & assessment' };
    else meta = { title: 'Tsela OAS', sub: '' };
  }

  // Close dropdown on outside click
  useEffect(() => {
    function handler(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  function handleLogout() {
    setMenuOpen(false);
    logout();
    navigate('/login', { replace: true });
  }

  return (
    <div
      className="dg-topbar"
      style={{
        position: 'fixed', top: 0, left: sidebarWidth, right: 0, height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 28px', zIndex: 99,
        transition: 'left 0.25s ease',
      }}
    >
      {/* Left — page title */}
      <div>
        <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 15, letterSpacing: '-0.2px' }}>
          {meta.title}
        </div>
        <div style={{ color: 'var(--text-tertiary)', fontSize: 11, marginTop: 1 }}>
          {meta.sub}
        </div>
      </div>

      {/* Right */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>

        {/* ICAO compliance label */}
        <div style={{ fontSize: 10, color: 'var(--text-tertiary)', letterSpacing: '0.04em' }}>
          ICAO Annex 14 · EUR Doc 015 · PANS-OPS
        </div>

        {/* Online pill */}
        <div style={{
          padding: '4px 10px',
          background: 'rgba(62,169,109,0.10)',
          border: '1px solid rgba(62,169,109,0.28)',
          borderRadius: 99,
          color: 'rgba(62,169,109,0.9)',
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
          display: 'flex', alignItems: 'center', gap: 5,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(62,169,109,0.9)', display: 'inline-block' }} />
          ONLINE
        </div>

        {/* Light / Dark toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 7,
          background: 'var(--glass-fill)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          padding: '4px 12px', borderRadius: 60,
          border: '1px solid var(--glass-border)',
        }}>
          <span style={{ fontSize: '1rem', lineHeight: 1 }}>☀️</span>
          <div
            onClick={toggleTheme}
            style={{
              position: 'relative', width: 44, height: 24, cursor: 'pointer',
              borderRadius: 24, flexShrink: 0,
              backgroundColor: isDark ? '#C6A744' : 'rgba(44,114,174,0.35)',
              boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.2)',
              transition: 'background-color 0.3s cubic-bezier(0.4,0,0.2,1)',
            }}
          >
            <div style={{
              position: 'absolute',
              width: 18, height: 18, top: 3,
              left: isDark ? 23 : 3,
              background: '#ffffff', borderRadius: '50%',
              boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
              transition: 'left 0.35s cubic-bezier(0.2,0.9,0.3,1.1)',
            }} />
          </div>
          <span style={{ fontSize: '1rem', lineHeight: 1 }}>🌙</span>
        </div>

        {/* Avatar + dropdown */}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <div
            onClick={() => setMenuOpen(o => !o)}
            title={user ? `${user.username} · ${user.role}` : ''}
            style={{
              width: 34, height: 34, borderRadius: '50%',
              background: 'linear-gradient(135deg, #2C72AE, #5F98C4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, color: 'white',
              border: '2px solid var(--glass-border)',
              flexShrink: 0, cursor: 'pointer',
              boxShadow: menuOpen ? '0 0 0 3px rgba(58,138,196,0.35)' : 'none',
              transition: 'box-shadow 0.15s',
            }}
          >
            {initials(user)}
          </div>

          {menuOpen && (
            <div style={{
              position: 'absolute', top: 42, right: 0, minWidth: 180,
              background: 'rgba(10,18,32,0.97)',
              backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(58,138,196,0.2)',
              borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
              overflow: 'hidden', zIndex: 200,
            }}>
              {/* User info header */}
              <div style={{ padding: '12px 14px', borderBottom: '1px solid rgba(58,138,196,0.12)' }}>
                <div style={{ color: '#f0f4fe', fontWeight: 700, fontSize: 13 }}>
                  {user?.full_name || user?.username}
                </div>
                <div style={{ color: 'rgba(148,168,200,0.55)', fontSize: 11, marginTop: 2, textTransform: 'capitalize' }}>
                  {user?.role}
                </div>
              </div>
              {/* Sign out */}
              <button
                onClick={handleLogout}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', padding: '10px 14px',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#f87171', fontSize: 13, textAlign: 'left',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(220,53,69,0.1)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
